/**
 * WEB COPY of DocumentsInputScreen.tsx — picked by the web bundler ahead of
 * the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Add a document with its details (web app Phase 5), as on the phone: a
 * name, category, type and tags, and either a file (encrypted in the
 * browser and stored, so the phone shows it too) or where the paper copy is
 * kept (a record in the vault). Also edits a paper-copy record. Stored
 * files are edited on their own page.
 */
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';

import backIcon from '../../../assets/icons/back.png';
import { vaultService } from '../../../services/vaultService';
import {
  cloudDocuments,
  displayNameFor,
  documentTypeFor,
  MAX_FILE_BYTES,
  newDocumentId,
} from '../../../services/documents/cloudDocuments.web';
import { ms, s, vs } from '../../../utils/responsive';
import { ChipGroup } from './DocumentsDataScreen.web';
import {
  CATEGORIES,
  DOCUMENT_TYPES,
  TAGS,
  categoryId,
  chooseFiles,
  errorMessage,
  formatBytes,
  withVaultKey,
} from './documentsShared.web';

export const DocumentsInputScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const editingId: string | undefined = route.params?.documentId;
  const existing = route.params?.existingDocument;

  const [name, setName] = useState<string>(existing?.documentName ?? '');
  const [category, setCategory] = useState<string>(
    categoryId(existing?.category ?? route.params?.category ?? 'other'),
  );
  const [documentType, setDocumentType] = useState<string>(existing?.documentType || 'PDF');
  const [tags, setTags] = useState<string[]>(Array.isArray(existing?.tags) ? existing.tags : []);
  const [file, setFile] = useState<File | null>(null);
  const [location, setLocation] = useState<string>(existing?.location ?? '');
  const [notes, setNotes] = useState<string>(existing?.notes ?? existing?.purpose ?? '');
  const [canStore, setCanStore] = useState<boolean | null>(editingId ? false : null);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState('');

  useEffect(() => {
    if (editingId) return; // a paper-copy record being edited
    cloudDocuments
      .list()
      .then((l) => setCanStore(l.limitBytes > 0))
      .catch(() => setCanStore(false));
  }, [editingId]);

  const pickFile = async () => {
    const [chosen] = await chooseFiles(false);
    if (!chosen) return;
    if (chosen.size > MAX_FILE_BYTES) {
      Alert.alert('This file is too large', 'Documents can be up to about 50MB each.');
      return;
    }
    setFile(chosen);
    setDocumentType(documentTypeFor(chosen.name, chosen.type));
    if (!name.trim()) setName(displayNameFor(chosen.name));
  };

  const returnTo = { root: 'Documents', routes: [{ name: 'DocumentsDashboardScreen' }] };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      Alert.alert('Missing information', 'Give the document a name.');
      return;
    }
    if (!file && !location.trim()) {
      Alert.alert(
        'Missing information',
        canStore ? 'Choose a file, or say where the paper copy is kept.' : 'Say where the paper copy is kept.',
      );
      return;
    }
    setSaving(true);
    try {
      if (file) {
        const ran = await withVaultKey(returnTo, async () => {
          await cloudDocuments.upload(
            file,
            { fileName: trimmed, category, documentType, tags },
            newDocumentId(),
            (st) => setStage(st === 'encrypting' ? 'Encrypting…' : st === 'uploading' ? 'Uploading…' : 'Saving…'),
          );
        });
        if (!ran) return;
      } else {
        await vaultService.storeDocumentMetadata(editingId ?? newDocumentId(), {
          category,
          documentType,
          documentName: trimmed,
          location: location.trim(),
          purpose: notes.trim() || undefined,
        });
      }
      navigation.navigate('DocumentsDashboardScreen', { category: undefined });
    } catch (error) {
      Alert.alert('Could not save the document', errorMessage(error, 'Try again.'));
    } finally {
      setSaving(false);
      setStage('');
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('DocumentsDashboardScreen'))}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{editingId ? 'Edit document' : 'Add a document'}</Text>
        <View style={styles.backButton} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.column}>
          <View style={styles.card}>
            <View>
              <Text style={styles.label}>Name</Text>
              <TextInput
                style={styles.input}
                value={name}
                onChangeText={setName}
                placeholder="For example, Last will and testament"
                placeholderTextColor="#9AA6B5"
                maxLength={255}
              />
            </View>
            <ChipGroup
              label="Category"
              options={CATEGORIES.map((c) => ({ value: c.id, label: c.name }))}
              value={category}
              onChange={setCategory}
            />
            <ChipGroup
              label="Type"
              options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
              value={documentType}
              onChange={setDocumentType}
            />
            <ChipGroup
              label="Tags"
              options={TAGS.map((t) => ({ value: t, label: t }))}
              values={tags}
              onToggle={(t) => setTags(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t])}
            />
          </View>

          {!editingId && (
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>The file</Text>
              {canStore === null ? (
                <ActivityIndicator color="#29BFC0" />
              ) : canStore ? (
                file ? (
                  <View style={styles.fileRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.fileName} numberOfLines={1}>
                        {file.name}
                      </Text>
                      <Text style={styles.muted}>{formatBytes(file.size)} · encrypted in this browser before it is stored</Text>
                    </View>
                    <TouchableOpacity onPress={() => setFile(null)}>
                      <Text style={styles.link}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity style={styles.secondaryButton} onPress={pickFile}>
                    <Text style={styles.secondaryButtonText}>Choose a file</Text>
                  </TouchableOpacity>
                )
              ) : (
                <Text style={styles.muted}>
                  Storing document files comes with Plus (500MB) and Premium (2GB). You can record
                  where the paper copy is kept below.
                </Text>
              )}
            </View>
          )}

          {!file && (
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>The paper copy</Text>
              <View>
                <Text style={styles.label}>Where is it kept?</Text>
                <TextInput
                  style={styles.input}
                  value={location}
                  onChangeText={setLocation}
                  placeholder="For example, the fire safe in the hall closet"
                  placeholderTextColor="#9AA6B5"
                />
              </View>
              <View>
                <Text style={styles.label}>Notes (optional)</Text>
                <TextInput
                  style={[styles.input, styles.multiline]}
                  value={notes}
                  onChangeText={setNotes}
                  multiline
                />
              </View>
            </View>
          )}

          <TouchableOpacity style={[styles.primaryButton, saving && { opacity: 0.6 }]} onPress={save} disabled={saving}>
            <Text style={styles.primaryButtonText}>{saving ? stage || 'Saving…' : 'Save document'}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F3F6F8' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: { padding: s(4), width: ms(32) },
  backIcon: { width: ms(24), height: ms(24), resizeMode: 'contain' },
  headerTitle: { fontSize: ms(16), fontWeight: '600', color: '#1A1A1A' },
  content: { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 120 },
  column: { width: '100%', maxWidth: 640, alignSelf: 'center' },
  card: { backgroundColor: '#FFFFFF', borderRadius: 16, padding: 20, marginBottom: 16, gap: 16 },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: '#1C2C4F' },
  label: { fontSize: 13, fontWeight: '600', color: '#6B7280', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#DDE3EA', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: '#1A1A1A' },
  multiline: { minHeight: 80, textAlignVertical: 'top' },
  muted: { fontSize: 14, lineHeight: 20, color: '#4B5563' },
  link: { fontSize: 14, color: '#0F8B8D', fontWeight: '600' },
  fileRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  fileName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  primaryButton: { backgroundColor: '#29BFC0', borderRadius: 24, paddingVertical: 14, alignItems: 'center' },
  primaryButtonText: { color: '#FFFFFF', fontWeight: '700', fontSize: 16 },
  secondaryButton: { alignSelf: 'flex-start', borderRadius: 24, paddingVertical: 11, paddingHorizontal: 20, borderWidth: 1.5, borderColor: '#1C2C4F' },
  secondaryButtonText: { color: '#1C2C4F', fontWeight: '600', fontSize: 15 },
});

export default DocumentsInputScreen;
